<template>
  <el-dialog
    title="Xem trước & Xuất báo cáo PDF"
    :visible.sync="dialogVisible"
    width="920px"
    append-to-body
    custom-class="print-preview-dialog"
    :before-close="handleClose"
  >
    <div class="dialog-toolbar">
      <div class="toolbar-info">
        <i class="el-icon-document"></i>
        <span>Định dạng chuẩn trang in A4 - Sẵn sàng lưu thành file PDF</span>
      </div>
      <div class="toolbar-actions">
        <el-button type="primary" icon="el-icon-printer" @click="handlePrint">
          In / Lưu PDF
        </el-button>
      </div>
    </div>

    <!-- Printable Report Paper -->
    <div id="printable-report" class="report-paper">
      <!-- 1. Header -->
      <div class="report-header">
        <div class="header-left">
          <div class="org-sub">HỆ THỐNG CƠ SỞ DỮ LIỆU & BẢN ĐỒ SỐ DU LỊCH</div>
          <div class="org-title">TRUNG TÂM QUẢN LÝ & XÚC TIẾN DU LỊCH</div>
          <div class="report-code">Mã số: BC-TK-{{ todayFormatted }}</div>
        </div>
        <div class="header-right">
          <div class="motto-top">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</div>
          <div class="motto-bottom">Độc lập - Tự do - Hạnh phúc</div>
          <div class="date-line">Thành phố Thủ Dầu Một, ngày {{ dayNow }} tháng {{ monthNow }} năm {{ yearNow }}</div>
        </div>
      </div>

      <div class="report-title-box">
        <h1 class="report-main-title">BÁO CÁO TỔNG QUAN HỆ THỐNG & PHÂN TÍCH TRUY CẬP</h1>
        <p class="report-sub-info">
          <span>Kỳ thống kê: <strong>{{ rangeLabel }}</strong></span>
          <span class="dot-sep">•</span>
          <span>Thời điểm kết xuất: <strong>{{ currentTime }}</strong></span>
        </p>
      </div>

      <!-- 2. Section I: KPI & Module Metrics -->
      <div class="report-section">
        <h2 class="section-title">I. BẢNG TỔNG HỢP CHỈ SỐ TRUY CẬP & THỐNG KÊ CÁC MODULE</h2>
        <table class="report-table">
          <thead>
            <tr>
              <th style="width: 50px;">STT</th>
              <th>Chỉ số đo lường / Module</th>
              <th style="width: 140px; text-align: right;">Giá trị thống kê</th>
              <th style="width: 110px; text-align: center;">Đơn vị tính</th>
              <th style="width: 150px;">Nguồn dữ liệu</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="text-center">1</td>
              <td class="font-bold">Người dùng hoạt động</td>
              <td class="text-right font-bold text-primary">{{ formatNumber(kpis.activeToday || 0) }}</td>
              <td class="text-center">Người dùng</td>
              <td>Google Analytics 4</td>
            </tr>
            <tr>
              <td class="text-center">2</td>
              <td class="font-bold">Lượt xem trang (Pageviews)</td>
              <td class="text-right font-bold">{{ formatNumber(kpis.totalPageViews || 0) }}</td>
              <td class="text-center">Lượt xem</td>
              <td>Google Analytics 4</td>
            </tr>
            <tr>
              <td class="text-center">3</td>
              <td class="font-bold">Tổng số lượt tương tác / Sự kiện</td>
              <td class="text-right font-bold">{{ formatNumber(kpis.totalVisits || 0) }}</td>
              <td class="text-center">Sự kiện</td>
              <td>Google Analytics 4</td>
            </tr>
            <tr>
              <td class="text-center">4</td>
              <td class="font-bold">Lượt quét mã QR tại điểm du lịch</td>
              <td class="text-right font-bold text-success">{{ formatNumber(kpis.totalQrScans || 0) }}</td>
              <td class="text-center">Lượt quét</td>
              <td>Google Analytics 4</td>
            </tr>
            <tr>
              <td class="text-center">5</td>
              <td class="font-bold">Lượt tra cứu tìm kiếm thông tin</td>
              <td class="text-right font-bold">{{ formatNumber(kpis.totalSearches || 0) }}</td>
              <td class="text-center">Lượt tìm kiếm</td>
              <td>Google Analytics 4</td>
            </tr>
            <tr v-for="(mod, idx) in moduleCards" :key="mod.key">
              <td class="text-center">{{ idx + 6 }}</td>
              <td class="font-bold">Module {{ mod.title }}</td>
              <td class="text-right font-bold">{{ formatNumber(getModuleTotal(mod.key)) }}</td>
              <td class="text-center">{{ mod.unit }}</td>
              <td>Cơ sở dữ liệu</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 3. Section II: Top Visited Pages & QR Code Scans -->
      <div class="report-section">
        <h2 class="section-title">II. TOP TRANG TRUY CẬP VÀ ĐIỂM QUÉT QR ĐƯỢC QUAN TÂM NHẤT</h2>
        <div class="two-tables-row">
          <div class="table-half">
            <h3 class="table-sub-title">Top trang có lượt xem cao nhất</h3>
            <table class="report-table">
              <thead>
                <tr>
                  <th style="width: 35px;">#</th>
                  <th>Trang / Đường dẫn</th>
                  <th style="width: 80px; text-align: right;">Lượt xem</th>
                  <th style="width: 65px; text-align: right;">Tỷ lệ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, idx) in (pageStats || []).slice(0, 5)" :key="idx">
                  <td class="text-center">{{ idx + 1 }}</td>
                  <td class="truncate" :title="p.title || p.path">{{ p.title || p.path }}</td>
                  <td class="text-right font-bold">{{ formatNumber(p.views || 0) }}</td>
                  <td class="text-right">{{ p.percentage || 0 }}%</td>
                </tr>
                <tr v-if="!pageStats || pageStats.length === 0">
                  <td colspan="4" class="text-center">Chưa có dữ liệu</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="table-half">
            <h3 class="table-sub-title">Hiệu quả quét mã QR tại điểm</h3>
            <table class="report-table">
              <thead>
                <tr>
                  <th style="width: 35px;">#</th>
                  <th>Điểm quét QR</th>
                  <th style="width: 80px; text-align: right;">Lượt quét</th>
                  <th style="width: 65px; text-align: right;">Tỷ lệ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(q, idx) in (qrStats || []).slice(0, 5)" :key="idx">
                  <td class="text-center">{{ idx + 1 }}</td>
                  <td class="truncate" :title="q.title || q.path">{{ q.title || q.path }}</td>
                  <td class="text-right font-bold">{{ formatNumber(q.scans || 0) }}</td>
                  <td class="text-right">{{ q.percentage || 0 }}%</td>
                </tr>
                <tr v-if="!qrStats || qrStats.length === 0">
                  <td colspan="4" class="text-center">Chưa có dữ liệu</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 4. Section III: Top 5 Featured Content across Modules -->
      <div class="report-section">
        <h2 class="section-title">III. TOP 5 BÀI NỔI BẬT THEO CÁC CHUYÊN MỤC DU LỊCH & TIN TỨC</h2>
        <div class="two-tables-row wrap-grid">
          <div v-for="tab in rankingTabs" :key="tab.key" class="table-half">
            <h3 class="table-sub-title">{{ tab.label }}</h3>
            <table class="report-table">
              <thead>
                <tr>
                  <th style="width: 35px;">#</th>
                  <th>Tên bài / Địa điểm</th>
                  <th style="width: 90px;">Danh mục</th>
                  <th style="width: 75px; text-align: right;">Lượt xem</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in getRankingList(tab.key)" :key="idx">
                  <td class="text-center">{{ idx + 1 }}</td>
                  <td class="font-bold truncate" :title="item.name || item.title">{{ item.name || item.title }}</td>
                  <td>{{ item.type || tab.defaultType }}</td>
                  <td class="text-right font-bold">{{ formatNumber(item.view_count || 0) }}</td>
                </tr>
                <tr v-if="getRankingList(tab.key).length === 0">
                  <td colspan="4" class="text-center">Chưa có dữ liệu</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 5. Signature Footer -->
      <div class="report-signatures">
        <div class="sig-block">
          <div class="sig-role">NGƯỜI LẬP BÁO CÁO</div>
          <div class="sig-note">(Ký, ghi rõ họ tên)</div>
          <div class="sig-space"></div>
          <div class="sig-name">Ban Quản Trị Hệ Thống</div>
        </div>
        <div class="sig-block">
          <div class="sig-role">LÃNH ĐẠO PHÊ DUYỆT</div>
          <div class="sig-note">(Ký, đóng dấu)</div>
          <div class="sig-space"></div>
          <div class="sig-name">Giám Đốc Trung Tâm</div>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Đóng</el-button>
      <el-button type="primary" icon="el-icon-printer" @click="handlePrint">
        In / Lưu PDF
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import dayjs from 'dayjs'
import { CMS_MODULE_CARDS } from './HomeKpiGrid.vue'
import { TOP_RANKING_TABS } from './HomeDataTableHub.vue'

export default {
  name: 'PrintReportDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    rangeLabel: {
      type: String,
      default: 'Toàn bộ thời gian'
    },
    kpis: {
      type: Object,
      default: () => ({})
    },
    cmsStats: {
      type: Object,
      default: () => ({})
    },
    pageStats: {
      type: Array,
      default: () => []
    },
    qrStats: {
      type: Array,
      default: () => []
    },
    topRankings: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      moduleCards: CMS_MODULE_CARDS,
      rankingTabs: TOP_RANKING_TABS
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    todayFormatted() {
      return dayjs().format('YYYYMMDD')
    },
    dayNow() {
      return dayjs().format('DD')
    },
    monthNow() {
      return dayjs().format('MM')
    },
    yearNow() {
      return dayjs().format('YYYY')
    },
    currentTime() {
      return dayjs().format('DD/MM/YYYY HH:mm:ss')
    }
  },
  methods: {
    getModuleTotal(key) {
      const mod = this.cmsStats && this.cmsStats[key]
      return (mod && mod.total) || 0
    },
    getRankingList(key) {
      const list = (this.topRankings && this.topRankings[key]) || []
      return list.slice(0, 5)
    },
    formatNumber(num) {
      if (num === undefined || num === null) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    handlePrint() {
      const printContents = document.getElementById('printable-report').innerHTML
      const printWindow = window.open('', '_blank', 'width=900,height=800')

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Bao_Cao_Tong_Quan_${dayjs().format('YYYYMMDD_HHmm')}</title>
            <style>
              @page {
                size: A4 portrait;
                margin: 15mm 15mm 15mm 15mm;
              }
              body {
                font-family: 'Times New Roman', Times, serif, Arial;
                color: #111827;
                background: #ffffff;
                margin: 0;
                padding: 10px;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .report-header {
                display: flex;
                justify-content: space-between;
                border-bottom: 2px solid #2b2d42;
                padding-bottom: 12px;
                margin-bottom: 16px;
              }
              .header-left .org-sub {
                font-size: 11px;
                font-weight: 700;
                color: #4b5563;
              }
              .header-left .org-title {
                font-size: 13px;
                font-weight: 800;
                color: #111827;
              }
              .header-left .report-code {
                font-size: 11px;
                font-style: italic;
                margin-top: 4px;
                color: #6b7280;
              }
              .header-right {
                text-align: center;
              }
              .header-right .motto-top {
                font-size: 11.5px;
                font-weight: 700;
              }
              .header-right .motto-bottom {
                font-size: 11px;
                font-weight: 700;
                text-decoration: underline;
              }
              .header-right .date-line {
                font-size: 10.5px;
                font-style: italic;
                margin-top: 4px;
                color: #4b5563;
              }
              .report-title-box {
                text-align: center;
                margin: 18px 0 22px 0;
              }
              .report-main-title {
                font-size: 18px;
                font-weight: 800;
                text-transform: uppercase;
                margin: 0 0 6px 0;
                color: #111827;
                letter-spacing: 0.5px;
              }
              .report-sub-info {
                font-size: 12px;
                color: #4b5563;
                margin: 0;
              }
              .dot-sep {
                margin: 0 8px;
              }
              .section-title {
                font-size: 13px;
                font-weight: 800;
                text-transform: uppercase;
                margin: 16px 0 8px 0;
                color: #1f2937;
                border-left: 3px solid #2563eb;
                padding-left: 8px;
              }
              .report-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 14px;
                font-size: 11px;
              }
              .report-table th, .report-table td {
                border: 1px solid #d1d5db;
                padding: 6px 8px;
              }
              .report-table th {
                background-color: #f3f4f6;
                font-weight: 700;
                color: #1f2937;
                text-align: left;
              }
              .two-tables-row {
                display: flex;
                gap: 14px;
              }
              .two-tables-row.wrap-grid {
                flex-wrap: wrap;
              }
              .table-half {
                flex: 1;
                min-width: 45%;
              }
              .table-sub-title {
                font-size: 11.5px;
                font-weight: 700;
                margin: 0 0 6px 0;
                color: #374151;
              }
              .text-center { text-align: center; }
              .text-right { text-align: right; }
              .font-bold { font-weight: 700; }
              .text-primary { color: #1d4ed8; }
              .text-success { color: #047857; }
              .truncate {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 180px;
              }
              .report-signatures {
                display: flex;
                justify-content: space-between;
                margin-top: 35px;
                padding: 0 40px;
                page-break-inside: avoid;
              }
              .sig-block {
                text-align: center;
              }
              .sig-role {
                font-size: 12px;
                font-weight: 700;
              }
              .sig-note {
                font-size: 10.5px;
                font-style: italic;
                color: #6b7280;
              }
              .sig-space {
                height: 60px;
              }
              .sig-name {
                font-size: 12px;
                font-weight: 700;
              }
            </style>
          </head>
          <body>
            ${printContents}
            <script>
              window.onload = function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              };
            <\/script>
          </body>
        </html>
      `)
      printWindow.document.close()
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  padding: 10px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;

  .toolbar-info {
    font-size: 13px;
    color: #475569;
    font-weight: 500;
    i {
      color: #3b82f6;
      margin-right: 6px;
    }
  }
}

.report-paper {
  background: #ffffff;
  padding: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  font-family: "Times New Roman", Times, serif;
  color: #111827;

  .report-header {
    display: flex;
    justify-content: space-between;
    border-bottom: 2px solid #1e293b;
    padding-bottom: 12px;
    margin-bottom: 16px;

    .header-left {
      .org-sub {
        font-size: 11px;
        font-weight: 700;
        color: #475569;
      }
      .org-title {
        font-size: 13px;
        font-weight: 800;
        color: #0f172a;
      }
      .report-code {
        font-size: 11px;
        font-style: italic;
        margin-top: 4px;
        color: #64748b;
      }
    }

    .header-right {
      text-align: center;
      .motto-top {
        font-size: 11.5px;
        font-weight: 700;
      }
      .motto-bottom {
        font-size: 11px;
        font-weight: 700;
        text-decoration: underline;
      }
      .date-line {
        font-size: 10.5px;
        font-style: italic;
        margin-top: 4px;
        color: #475569;
      }
    }
  }

  .report-title-box {
    text-align: center;
    margin: 18px 0 20px 0;

    .report-main-title {
      font-size: 18px;
      font-weight: 800;
      text-transform: uppercase;
      margin: 0 0 6px 0;
      color: #0f172a;
      letter-spacing: 0.5px;
    }
    .report-sub-info {
      font-size: 12px;
      color: #475569;
      margin: 0;

      .dot-sep {
        margin: 0 8px;
      }
    }
  }

  .section-title {
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    margin: 16px 0 8px 0;
    color: #1e293b;
    border-left: 3px solid #2563eb;
    padding-left: 8px;
  }

  .report-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 14px;
    font-size: 11.5px;

    th, td {
      border: 1px solid #cbd5e1;
      padding: 6px 8px;
    }

    th {
      background-color: #f1f5f9;
      font-weight: 700;
      color: #1e293b;
      text-align: left;
    }
  }

  .two-tables-row {
    display: flex;
    gap: 16px;

    &.wrap-grid {
      flex-wrap: wrap;
    }

    .table-half {
      flex: 1;
      min-width: 45%;

      .table-sub-title {
        font-size: 11.5px;
        font-weight: 700;
        margin: 0 0 6px 0;
        color: #334155;
      }
    }
  }

  .text-center { text-align: center; }
  .text-right { text-align: right; }
  .font-bold { font-weight: 700; }
  .text-primary { color: #2563eb; }
  .text-success { color: #059669; }

  .truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
    display: inline-block;
  }

  .report-signatures {
    display: flex;
    justify-content: space-between;
    margin-top: 30px;
    padding: 0 40px;

    .sig-block {
      text-align: center;

      .sig-role {
        font-size: 12px;
        font-weight: 700;
      }
      .sig-note {
        font-size: 10.5px;
        font-style: italic;
        color: #64748b;
      }
      .sig-space {
        height: 55px;
      }
      .sig-name {
        font-size: 12px;
        font-weight: 700;
      }
    }
  }
}
</style>
